import type { TFunction } from "i18next";

import type { RoadmapResponse } from "../api/client";

/**
 * The Career Transition landing's primary CTA. A user who already has a
 * roadmap is sent to view it; everyone else starts the assessment. Keeps the
 * landing from telling a returning user to "generate" a roadmap they have.
 *
 * Takes `t` (not useTranslation) because this is a plain function, not a
 * component — same TFunction-parameter pattern as nextMasteryModule below
 * (I18N_PLAN.md Phase 2). Caught by the pseudo-locale screenshot pass: this
 * file is a .ts util, so the ratchet-driven .tsx cards never saw it.
 */
export function ctHeroCta(
  roadmap: RoadmapResponse | undefined,
  t: TFunction,
): { label: string; href: string } {
  return roadmap
    ? { label: t("CareerTransitionHome.heroCtaViewRoadmap"), href: `/career-transition/roadmap/${roadmap.id}` }
    : {
        label: t("CareerTransitionHome.heroCtaStartAssessment"),
        href: "/career-transition/assessment",
      };
}

/**
 * The Agentic Mastery landing's hero CTA. A user mid-curriculum is pointed at
 * their first incomplete module ("Continue: …"); a fresh user starts Module 1.
 * `modules` is the curriculum's modules in order; `progress` carries the set of
 * completed module slugs and the aggregate completed count.
 */
export function nextMasteryModule(
  modules: { slug: string; title: string }[],
  progress: { completedSlugs: string[]; counts: { completed: number } } | undefined,
  t: TFunction,
): { label: string; slug: string } {
  const first = modules[0];
  const started = (progress?.counts.completed ?? 0) > 0;
  if (!started || modules.length === 0) {
    return { label: t("AgenticMasteryHome.heroCtaStartModule1"), slug: first?.slug ?? "foundations" };
  }
  const done = new Set(progress!.completedSlugs);
  const next = modules.find((m) => !done.has(m.slug)) ?? first;
  return { label: t("AgenticMasteryHome.heroCtaContinueModule", { title: next.title }), slug: next.slug };
}
